import { API_METHODS } from '@compass/client/constants/api-methods';
import type {
  CreateCaseRecordRequest,
  CreateCaseRecordResponse,
} from '@compass/client/types/case_record';
import { FILE_UPLOAD_TIMEOUT, getApiPath, transport } from '@services/api.config';

/**
 * Creates a case record together with attached File-type field uploads in a single multipart
 * request (Manual Case Creation flow). The case JSON goes in the "input" part; each picked file goes
 * in a "file:<field_id>" part. The backend creates the case, uploads the files to Arcus, and stores
 * the asset IDs in input_data[field_id] — rolling everything back if any upload fails.
 *
 * This stays hand-written (not generated) because the generated createCaseRecord client emits
 * one flat FormData from the proto's own fields and cannot express the nested
 * "file:<field_id>" parts. Only the HTTP call is shared: it goes through the transport's
 * postForm (BV-1874), which owns the Content-Type/boundary handling, rather than repeating
 * axios's transformRequest dance here.
 */
export const createCaseRecordWithFiles = async (
  request: Omit<CreateCaseRecordRequest, 'files'>,
  files: Record<string, File | File[]>,
): Promise<CreateCaseRecordResponse> => {
  const { organization_id, program_id, ...body } = request;
  const url = getApiPath(API_METHODS.CaseRecordsService.CreateCaseRecord, {
    organization_id,
    program_id,
  });

  const formData = new FormData();
  // organization_id/program_id are taken from the path; everything else (workflow_id, reference_id,
  // input_data) is the JSON the backend parses into CreateCaseRecordRequest.
  formData.append('input', JSON.stringify(body));
  // Multi-file fields (schema fileAllowMultiple) send several parts under the same
  // "file:<field_id>" name; the backend stores their asset IDs as a list.
  for (const [fieldId, value] of Object.entries(files)) {
    for (const file of Array.isArray(value) ? value : [value]) {
      formData.append(`file:${fieldId}`, file, file.name);
    }
  }

  return transport.postForm<CreateCaseRecordResponse>(url, formData, {
    timeout: FILE_UPLOAD_TIMEOUT,
  });
};
